/**
 * AuthContext.tsx — MER Algo Footprints
 *
 * Single source of truth for authentication state.
 *
 * ── Session shape ──────────────────────────────────────────────────────────
 * Mirrors server/authRoutes.js: { id, email, tier }
 * tier: 'core' | 'byob' | 'premium'  (matches DB CHECK constraint)
 * role: 'user' | 'admin'  (authorization, separate from tier)
 *
 * ── Entitlement shape ──────────────────────────────────────────────────────
 * AppEntitlement wraps the session user with a derived subscriber flag, taken
 * solely from the server's authoritative `commerciallyEntitled` answer
 * (Phase-2 P2-D13 IMPL-3). The market-data tier is not commercial access.
 *
 * ── Bootstrap flow ─────────────────────────────────────────────────────────
 * On mount:
 *   1. GET /api/me → authenticated | unauthenticated
 *   2. If network failure: retry once after 800ms
 *   3. If still failing: status = 'error' (not 'unauthenticated')
 *      Error state is distinguishable from a clean logged-out state.
 *
 * Status values:
 *   loading        — bootstrap in progress (prevents route flicker)
 *   authenticated  — valid session found
 *   unauthenticated— no session (clean logout or first visit)
 *   error          — bootstrap failed after retry (network/server error)
 *
 * On login success: updates user in-place (no reload)
 * On logout: POST /api/auth/logout → clears user → status = 'unauthenticated'
 */

import React, {
  createContext, useContext, useState, useEffect, useCallback, ReactNode,
} from 'react';

// ── Types ─────────────────────────────────────────────────────────────────────

// Tier values match the database CHECK constraint in migration 001
export type UserTier = 'core' | 'byob' | 'premium';
// Role controls authorization (separate from tier/plan)
export type UserRole = 'user' | 'admin';

export interface SessionUser {
  id:    number;
  email: string;
  role:  UserRole;
  tier:  UserTier;
  // Phase-2 P2-D5. Present on /api/me; absent from the login and registration
  // responses, which is why it is optional. Recorded identity assurance only —
  // it MUST NOT gate routing, entitlement or any capability (D-P2AC-1).
  emailVerified?: boolean;
  // Phase-2 P2-D1. Whether this account has a LOCAL password credential, which
  // a Google-provisioned account may legitimately not have. Present on
  // /api/me only, hence optional.
  //
  // It decides whether the Change Password surface is APPLICABLE — not whether
  // the user is permitted to do anything. It FAILS CLOSED: absent or undefined
  // is NOT treated as true, matching the server's own fail-closed default, so
  // the surface appears only on a positively proven local credential. D-P2AF-4
  // forbids presenting Change Password to a passwordless account without
  // qualification, and an unknown capability is not a proven one.
  hasPasswordCredential?: boolean;
  // Phase-2 P2-D6. Whether a Google identity is already associated with this
  // account, and whether starting a link ceremony is currently permitted.
  //
  // Both FAIL CLOSED for the same reason as hasPasswordCredential: absent or
  // undefined is NOT true. `canLinkGoogle` is computed entirely server-side —
  // it already folds in the feature flag, the provider configuration, the
  // credential type and whether an identity exists — so the client never
  // recomputes the policy, it only renders the answer.
  //
  // There is deliberately no `canUnlinkGoogle`: unlink is P2-D6 IMPL-4, and a
  // field that existed here would invite a control that must not exist yet.
  hasGoogleIdentity?: boolean;
  canLinkGoogle?: boolean;
  // Phase-2 P2-D6 IMPL-4. Also fail-closed, and also entirely server-computed:
  // it folds in the feature flag, an existing identity, and the local password
  // the unlink ceremony reauthenticates against.
  canUnlinkGoogle?: boolean;
  // Phase-2 P2-D14 slice 2. Whether this account may permanently disable
  // ITSELF, and the strictest of these flags because the action it describes
  // cannot be undone by anyone — not the user, not an administrator.
  //
  // Entirely server-computed. It folds in active state, the terminal marker,
  // whether the account is password-backed (the ceremony reauthenticates with
  // the local password) and the sole-active-administrator invariant. The
  // client MUST NOT recompute any of that: a destructive control is the worst
  // possible place for the client and the server to disagree.
  //
  // FAILS CLOSED exactly like its siblings — absent or undefined is NOT true.
  // And it is only ever a HINT. The server re-evaluates every condition inside
  // the authoritative transaction, because a capability computed at render
  // time is stale by definition.
  canDisableAccount?: boolean;
  // Phase-2 P2-D13 IMPL-3. The server's authoritative commercial-entitlement
  // answer, and the ONLY input to `subscriber`. A tri-state:
  //
  //   true  — the read completed and the account is entitled
  //   false — the read completed and the account is not entitled
  //   null  — the server could not determine it
  //
  // Optional because only /api/me carries it; the login and registration
  // responses do not. Absent, null and false all grant nothing, and there is
  // deliberately NO fallback to `tier` or `role` — that fallback is the
  // coupling this field exists to remove.
  commerciallyEntitled?: boolean | null;
}

// Phase-2 P2-D13 IMPL-4 (D-P2AT-26). The client's explicit commercial-access
// state, so that "not answered yet", "could not be answered" and "answered no"
// can never be confused. Authentication loading is a separate axis (AuthStatus).
export type EntitlementState = 'unknown' | 'entitled' | 'not_entitled' | 'unavailable';

export interface AppEntitlement {
  authenticated:    boolean;
  subscriber:       boolean;           // true ONLY when commerciallyEntitled === true
  entitlementState: EntitlementState;
  isAdmin:          boolean;           // true when role is 'admin'
  plan:             'none' | 'core' | 'byob' | 'premium';
}

export type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated' | 'error';

export interface AuthState {
  status:          AuthStatus;
  user:            SessionUser | null;
  entitlement:     AppEntitlement;
  login:           (email: string, password: string) => Promise<{ ok: boolean; reason?: string }>;
  // Phase-2 P2-D13 IMPL-0 adds `detail`: a bounded, server-authored, already
  // safe sentence carried alongside the reason code so a caller can explain a
  // refusal instead of guessing at one. Declared here and not merely returned,
  // because a consumer typed against this interface could not otherwise read
  // it — an undeclared field is not a contract.
  register:        (email: string, password: string) =>
                     Promise<{ ok: boolean; reason?: string; detail?: string }>;
  logout:          () => Promise<void>;
  refreshSession:  () => Promise<void>;  // explicit re-bootstrap (e.g. after tab focus)
  // Phase-2 P2-D13 IMPL-4. Re-reads GET /api/me WITHOUT passing through the
  // 'loading' status, so the tree stays mounted — the access gate relies on
  // that to keep its retry budget. See the implementation for failure handling.
  refreshEntitlement: () => Promise<void>;
  // Phase-2 P2-D4. `reauthenticationRequired` means the change COMMITTED but the
  // session could not be re-stamped, so the caller must sign in again — it is a
  // qualified success, never an error.
  changePassword:  (currentPassword: string, newPassword: string) =>
                     Promise<{ ok: boolean; reason?: string; detail?: string; reauthenticationRequired?: boolean }>;
  // Phase-2 P2-D5. Request is also resend: a reissue supersedes prior tokens,
  // so there is no separate resend call (D-P2AC-9).
  requestEmailVerification: () =>
                     Promise<{ ok: boolean; reason?: string; alreadyVerified?: boolean }>;
  // Phase-2 P2-D6. Two halves of one ceremony, kept separate because they are
  // separated by a round trip through Google.
  //
  // `startGoogleLink` reauthenticates with the current MER password and returns
  // the authorization URL the browser must navigate to. `finalizeGoogleLink` is
  // the same-origin completion after Google redirects back, and it is the only
  // step that creates the association.
  startGoogleLink: (currentPassword: string) =>
                     Promise<{ ok: boolean; authorizationUrl?: string; reason?: string }>;
  finalizeGoogleLink: () =>
                     Promise<{ ok: boolean; reason?: string; alreadyLinked?: boolean; reauthenticationRequired?: boolean }>;
  // Phase-2 P2-D6 IMPL-4. One request, no provider round trip. A successful
  // unlink REVOKES every other session, so `reauthenticationRequired` here
  // means the revocation committed and this session could not be re-stamped —
  // a qualified success, never a failure.
  unlinkGoogle: (currentPassword: string) =>
                     Promise<{ ok: boolean; reason?: string; alreadyUnlinked?: boolean; reauthenticationRequired?: boolean }>;
  // Phase-2 P2-D14 slice 3. Permanently disable the CALLER'S OWN account.
  //
  // There is deliberately no qualified-success variant here, unlike every
  // other action above. The server returns success whenever the database
  // committed, and it commits BEFORE any session teardown is attempted — so if
  // the session store could not be cleaned up the account is still terminal
  // and the answer is still `ok`. A `reauthenticationRequired` flag would
  // imply the caller might come back, and they cannot.
  disableAccount: (currentPassword: string) =>
                     Promise<{ ok: boolean; reason?: string }>;
}

// ── Entitlement derivation ────────────────────────────────────────────────────
// All route guards and feature gates read from entitlement — never from tier directly.
//
// Phase-2 P2-D13 IMPL-3. `subscriber` is the commercial-access signal and comes
// from the server's authoritative answer ALONE. It used to be derived from the
// market-data tier, which made one column mean two unrelated things (D-P2AR-37).
// `=== true` is the whole rule: undetermined, absent or malformed grants nothing.
//
// `isAdmin` and `plan` are reported as what they are — role, for administrative
// surfaces, and the market-data tier, for display — and neither confers
// commercial access. Future billing grants through the same server axis, so
// this function should not need to change when it arrives.

//
// Phase-2 P2-D13 IMPL-4 adds `entitlementState`, mapped from the same field:
//
//   true       → 'entitled'
//   false      → 'not_entitled'
//   null       → 'unavailable'   (the server could not determine it)
//   absent     → 'unknown'       (not hydrated from /api/me yet — NEVER false)
//   malformed  → 'unavailable'   (present but not a banked value: undeterminable)
//
// Nothing else is consulted: not role, not tier, not beta, not a prior value.

function entitlementStateOf(user: SessionUser | null): EntitlementState {
  if (!user) return 'unknown';
  const value = user.commerciallyEntitled;
  if (value === true)      return 'entitled';
  if (value === false)     return 'not_entitled';
  if (value === undefined) return 'unknown';
  return 'unavailable';
}

function deriveEntitlement(user: SessionUser | null): AppEntitlement {
  if (!user) {
    return { authenticated: false, subscriber: false, entitlementState: 'unknown', isAdmin: false, plan: 'none' };
  }
  const subscriber = user.commerciallyEntitled === true;
  const isAdmin    = user.role === 'admin';
  return {
    authenticated: true,
    subscriber,
    entitlementState: entitlementStateOf(user),
    isAdmin,
    plan: user.tier,
  };
}

// ── Session fetch with one bounded retry ──────────────────────────────────────
// Retry only on network errors (fetch throws), not on 401/403 (clean non-auth).
// Max 2 total attempts with an 800ms pause before the retry.
// Returns { ok, user } or throws if both attempts fail.

const RETRY_DELAY_MS = 800;

async function fetchSession(): Promise<{ ok: boolean; user?: SessionUser }> {
  const attempt = async () => {
    const r = await fetch('/api/me', { credentials: 'include' });
    // Phase-2 P2-D3: 503 AUTH_UNAVAILABLE is NOT "signed out". The server could
    // not read the authoritative credential epoch, so it refused the request and
    // deliberately PRESERVED the session. Treating it as unauthenticated would
    // bounce the user to sign-in over a transient database blip and would defeat
    // the whole point of preserving the session. It is thrown so the existing
    // retry path handles it, and a persistent failure surfaces as 'error'
    // rather than as a logout.
    if (r.status === 503) throw new Error('AUTH_UNAVAILABLE');
    return r.json() as Promise<{ ok: boolean; user?: SessionUser }>;
  };

  try {
    return await attempt();
  } catch {
    // Network error or a transient 503 on the first attempt → wait and retry once
    await new Promise(res => setTimeout(res, RETRY_DELAY_MS));
    return await attempt(); // throws if still failing → caller sets 'error'
  }
}

// ── Post-authentication hydration ─────────────────────────────────────────────
// Phase-2 P2-D13 IMPL-4 (D-P2AT-26). A sign-in response carries identity but
// not the commercial answer, and entitlement is deliberately NOT duplicated into
// authentication responses. So after a successful sign-in the canonical
// GET /api/me is read BEFORE the authenticated state is settled — otherwise an
// entitled user would briefly hold no answer, and an active gate would flash a
// denial at them.
//
// If /api/me cannot be read, the sign-in still succeeded: the fallback is the
// sign-in user, whose field is absent and therefore 'unknown' — never false.

async function hydrateSessionUser(fallback: SessionUser): Promise<SessionUser> {
  try {
    const data = await fetchSession();
    if (data.ok && data.user) return data.user;
  } catch {
    // fall through to the fallback
  }
  return fallback;
}

// ── Context ───────────────────────────────────────────────────────────────────

const AuthContext = createContext<AuthState | null>(null);

// ── Phase-2 P2-D7: CSRF token ─────────────────────────────────────────────────
// The server binds a synchronizer token to the session and requires it on
// state-changing auth routes. A fresh token is fetched immediately before each
// such request rather than cached, because a successful login or registration
// regenerates the session and discards the token along with the rest of the
// pre-authentication session state.
// Exported for Phase-2 P2-D3: the password-reset pages are outside the auth
// provider but post to the same CSRF-guarded surface, and must fetch a token
// the same way rather than reimplementing it.
/**
 * Phase-2 P2-D14 slice 3. The confirmation phrase, defined ONCE on the client.
 *
 * It must equal `SELF_DISABLE_CONFIRMATION` in `server/authRoutes.js`
 * character for character: the server compares `req.body.confirm` against its
 * own constant, so a phrase that drifted by one character would produce a
 * permanent 400 that no amount of client-side testing would explain. That
 * cross-file equality is pinned by `p2D14SelfServiceLifecycleUi.test.js` (U9).
 *
 * Exported so the page can DISPLAY the exact string the user must type,
 * instead of hard-coding a second copy that could drift from this one.
 */
export const ACCOUNT_DISABLE_CONFIRMATION = 'PERMANENTLY_DISABLE_MY_ACCOUNT';

export async function csrfHeader(): Promise<Record<string, string>> {
  try {
    const r = await fetch('/api/csrf-token', { credentials: 'include' });
    const d = await r.json();
    return typeof d?.csrfToken === 'string' ? { 'x-csrf-token': d.csrfToken } : {};
  } catch {
    return {};
  }
}


export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [user,   setUser]   = useState<SessionUser | null>(null);

  // ── Bootstrap ─────────────────────────────────────────────────────────────
  const bootstrap = useCallback(async () => {
    setStatus('loading');
    try {
      const data = await fetchSession();
      if (data.ok && data.user) {
        setUser(data.user);
        setStatus('authenticated');
      } else {
        // Clean unauthenticated (server responded with ok: false)
        setUser(null);
        setStatus('unauthenticated');
      }
    } catch {
      // Both attempts failed — server unreachable or unexpected error
      setUser(null);
      setStatus('error');
    }
  }, []);

  useEffect(() => { bootstrap(); }, [bootstrap]);

  // refreshSession: explicit re-bootstrap (e.g. after tab re-focus, or after plan upgrade)
  const refreshSession = useCallback(async () => {
    await bootstrap();
  }, [bootstrap]);

  // Phase-2 P2-D13 IMPL-4. A silent re-read of /api/me for the access gate.
  //
  // It never sets 'loading': that would unmount everything below ProtectedRoute,
  // reset the gate's single automatic retry and turn a bounded retry into a
  // loop. Outcomes:
  //   · /api/me answers with a user → install it (its field is authoritative);
  //   · /api/me says there is no session → signed out, as bootstrap would;
  //   · /api/me cannot be read at all → the answer is undeterminable, recorded
  //     as `null` ('unavailable') so the gate shows "Unable to verify access"
  //     rather than spinning on 'unknown' forever. Identity is untouched.
  const refreshEntitlement = useCallback(async () => {
    try {
      const data = await fetchSession();
      if (data.ok && data.user) {
        setUser(data.user);
      } else {
        setUser(null);
        setStatus('unauthenticated');
      }
    } catch {
      setUser(prev => (prev ? { ...prev, commerciallyEntitled: null } : prev));
    }
  }, []);

  // ── Login ─────────────────────────────────────────────────────────────────
  const login = useCallback(async (email: string, password: string) => {
    try {
      const r = await fetch('/api/auth/login', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', ...(await csrfHeader()) },
        credentials: 'include',
        body: JSON.stringify({ email, password }),
      });
      const data = await r.json();
      if (data.ok && data.user) {
        // Phase-2 P2-D13 IMPL-4: settle only once /api/me has answered.
        const hydrated = await hydrateSessionUser(data.user);
        setUser(hydrated);
        setStatus('authenticated');
        return { ok: true };
      }
      return { ok: false, reason: data.reason ?? 'LOGIN_FAILED' };
    } catch {
      return { ok: false, reason: 'NETWORK_ERROR' };
    }
  }, []);

  // ── Register ──────────────────────────────────────────────────────────────
  const register = useCallback(async (email: string, password: string) => {
    try {
      const r = await fetch('/api/auth/register', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', ...(await csrfHeader()) },
        credentials: 'include',
        body: JSON.stringify({ email, password }),
      });
      const data = await r.json();
      if (data.ok && data.user) {
        // Same settle-after-/api/me rule as sign-in (registration stays disabled).
        const hydrated = await hydrateSessionUser(data.user);
        setUser(hydrated);
        setStatus('authenticated');
        return { ok: true };
      }
      // Phase-2 P2-D13 IMPL-0. `detail` used to be discarded here, and that
      // was the middle link of a three-link defect chain: the server answers a
      // closed-registration attempt with `REGISTRATION_DISABLED` AND a correct
      // explanatory sentence, this function threw the sentence away, and the
      // consumer — having no mapping for the reason — fell through to
      // "Registration failed. Please try again." for a policy refusal that
      // retrying can never satisfy.
      //
      // The reason code remains the thing consumers branch on; `detail` is a
      // bounded, server-authored, already-safe sentence carried alongside it
      // so a caller can explain rather than guess. No status code, no raw
      // error and no server internal is exposed.
      return {
        ok: false,
        reason: data.reason ?? 'REGISTER_FAILED',
        detail: typeof data.detail === 'string' ? data.detail : undefined,
      };
    } catch {
      return { ok: false, reason: 'NETWORK_ERROR' };
    }
  }, []);

  // ── Logout ────────────────────────────────────────────────────────────────
  // Server-side session destroy + client state clear.
  // ProtectedRoute will redirect to /login once status = 'unauthenticated'.
  const logout = useCallback(async () => {
    try {
      await fetch('/api/auth/logout', {
        method: 'POST',
        headers: { ...(await csrfHeader()) },
        credentials: 'include',
      });
    } catch { /* best-effort — clear client state regardless */ }
    setUser(null);
    setStatus('unauthenticated');
  }, []);

  // ── Change password (P2-D4) ───────────────────────────────────────────────
  // Passwords live in the caller's component state and nowhere else: never in a
  // URL, never in storage, never logged. On a qualified success the server has
  // already destroyed the session, so client state is cleared to match rather
  // than left claiming an authentication that no longer exists.
  const changePassword = useCallback(async (currentPassword: string, newPassword: string) => {
    try {
      const r = await fetch('/api/auth/password/change', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', ...(await csrfHeader()) },
        credentials: 'include',
        body: JSON.stringify({ currentPassword, newPassword }),
      });
      const data = await r.json().catch(() => ({}));
      if (r.ok && data?.ok) {
        if (data.reauthenticationRequired) {
          setUser(null);
          setStatus('unauthenticated');
          return { ok: true, reauthenticationRequired: true };
        }
        return { ok: true };
      }
      return { ok: false, reason: data?.reason ?? 'PASSWORD_CHANGE_FAILED', detail: data?.detail };
    } catch {
      return { ok: false, reason: 'NETWORK_ERROR' };
    }
  }, []);

  // ── Request / resend email verification (P2-D5) ───────────────────────────
  // Deliberately does not touch `status` or `user`: verification grants nothing
  // and must never affect whether the app considers someone authenticated.
  const requestEmailVerification = useCallback(async () => {
    try {
      const r = await fetch('/api/auth/email-verification/request', {
        method:  'POST',
        headers: { ...(await csrfHeader()) },
        credentials: 'include',
      });
      const data = await r.json().catch(() => ({}));
      if (r.ok && data?.ok) {
        return { ok: true, alreadyVerified: Boolean(data.alreadyVerified) };
      }
      return { ok: false, reason: data?.reason ?? 'VERIFICATION_REQUEST_FAILED' };
    } catch {
      return { ok: false, reason: 'NETWORK_ERROR' };
    }
  }, []);

  // ── Google account linking (P2-D6) ────────────────────────────────────────
  // The browser receives ONLY the authorization URL. The PKCE verifier, the
  // nonce, the correlation secret, the bound user id and the epoch snapshot all
  // stay server-side, exactly as for D1 login.
  const startGoogleLink = useCallback(async (currentPassword: string) => {
    try {
      const r = await fetch('/api/auth/google/link', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', ...(await csrfHeader()) },
        credentials: 'include',
        body: JSON.stringify({ currentPassword }),
      });
      const data = await r.json().catch(() => ({}));
      if (r.ok && data?.ok && typeof data.authorizationUrl === 'string') {
        return { ok: true, authorizationUrl: data.authorizationUrl };
      }
      return { ok: false, reason: data?.reason ?? 'GOOGLE_LINK_UNAVAILABLE' };
    } catch {
      return { ok: false, reason: 'NETWORK_ERROR' };
    }
  }, []);

  // Completing the ceremony. `reauthenticationRequired` is a QUALIFIED SUCCESS:
  // the identity association is durable and the session could not be
  // re-stamped, so the caller must sign in again. It is never an error — the
  // same rule banked for P2-D4 at D-P2AA-4.
  const finalizeGoogleLink = useCallback(async () => {
    try {
      const r = await fetch('/api/auth/google/link/finalize', {
        method:  'POST',
        headers: { ...(await csrfHeader()) },
        credentials: 'include',
      });
      const data = await r.json().catch(() => ({}));
      if (r.ok && data?.ok) {
        return {
          ok: true,
          alreadyLinked: Boolean(data.alreadyLinked),
          reauthenticationRequired: Boolean(data.reauthenticationRequired),
        };
      }
      return { ok: false, reason: data?.reason ?? 'GOOGLE_LINK_REFUSED' };
    } catch {
      return { ok: false, reason: 'NETWORK_ERROR' };
    }
  }, []);

  const unlinkGoogle = useCallback(async (currentPassword: string) => {
    try {
      const r = await fetch('/api/auth/google/unlink', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', ...(await csrfHeader()) },
        credentials: 'include',
        body: JSON.stringify({ currentPassword }),
      });
      const data = await r.json().catch(() => ({}));
      if (r.ok && data?.ok) {
        return {
          ok: true,
          alreadyUnlinked: Boolean(data.alreadyUnlinked),
          reauthenticationRequired: Boolean(data.reauthenticationRequired),
        };
      }
      return { ok: false, reason: data?.reason ?? 'GOOGLE_UNLINK_REFUSED' };
    } catch {
      return { ok: false, reason: 'NETWORK_ERROR' };
    }
  }, []);

  // ── Permanently disable this account (P2-D14 slice 3) ─────────────────────
  //
  // The password lives in the caller's component state and is passed straight
  // through: never stored here, never logged, never in a URL.
  //
  // ON SUCCESS THE CLIENT STATE IS CLEARED WITHOUT CALLING LOGOUT. The server
  // already destroyed the session inside the request, so POSTing
  // /api/auth/logout would be a second call that can only fail — and §11
  // forbids treating it as a prerequisite to success anyway. Clearing directly
  // makes the client model match a server state that is already true.
  //
  // A thrown fetch becomes NETWORK_ERROR and nothing more. This action is NOT
  // repeatable, so an ambiguous transport failure means the commit may already
  // have happened: the caller must not be told the account is active, must not
  // be told it was disabled, and must never be retried automatically.
  const disableAccount = useCallback(async (currentPassword: string) => {
    try {
      const r = await fetch('/api/auth/account/permanent-disable', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', ...(await csrfHeader()) },
        credentials: 'include',
        body: JSON.stringify({
          currentPassword,
          // The server-authorized constant, NOT whatever the user typed. What
          // they typed is a UX confirmation; this is the contract.
          confirm: ACCOUNT_DISABLE_CONFIRMATION,
        }),
      });
      const data = await r.json().catch(() => ({}));
      if (r.ok && data?.ok) {
        setUser(null);
        setStatus('unauthenticated');
        return { ok: true };
      }
      if (r.status === 429) return { ok: false, reason: 'RATE_LIMITED' };
      return { ok: false, reason: data?.reason ?? 'ACCOUNT_DISABLE_REFUSED' };
    } catch {
      return { ok: false, reason: 'NETWORK_ERROR' };
    }
  }, []);

  const value: AuthState = {
    status,
    user,
    entitlement: deriveEntitlement(user),
    login,
    register,
    logout,
    refreshSession,
    refreshEntitlement,
    changePassword,
    requestEmailVerification,
    startGoogleLink,
    finalizeGoogleLink,
    unlinkGoogle,
    disableAccount,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
